import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Users, UserCheck, Search, UserPlus } from 'lucide-react';
import { useFollowersList } from '@/hooks/useFollowersList';
import { useUserFollowersForList } from '@/hooks/useUserFollowersForList';
import { useFriends } from '@/hooks/useFriends';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface FollowersListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId?: string; // If provided, show followers for this specific user
}

export function FollowersListDialog({ open, onOpenChange, userId }: FollowersListDialogProps) {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const isOwnProfile = !userId || userId === authUser?.id;
  
  // Use appropriate hook based on whether it's own profile or another user's
  const { followers: ownFollowers, loading: ownLoading } = useFollowersList();
  const { followers: otherFollowers, loading: otherLoading } = useUserFollowersForList(userId);
  
  const followers = isOwnProfile ? ownFollowers : otherFollowers;
  const loading = isOwnProfile ? ownLoading : otherLoading;
  
  const { followUser, getRelationshipStatus } = useFriends();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFollowers = useMemo(() => {
    if (!followers) return [];
    
    return followers.filter(follower => 
      follower.full_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [followers, searchQuery]);

  const handleFollowBack = async (followerId: string) => {
    try {
      await followUser(followerId);
    } catch (error) {
      console.error('Error following user:', error);
    }
  };

  const handleViewProfile = (followerId: string) => {
    navigate(`/profile/${followerId}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Seguidores
          </DialogTitle>
        </DialogHeader>
        
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar por nombre..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8">Cargando seguidores...</div>
          ) : filteredFollowers && filteredFollowers.length > 0 ? (
            filteredFollowers.map((follower) => (
              <Card key={follower.user_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                     <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => handleViewProfile(follower.user_id)}>
                       <Avatar className="h-10 w-10">
                         <AvatarImage src={follower.avatar_url || undefined} alt={follower.full_name} />
                         <AvatarFallback className="bg-gradient-to-br from-primary to-primary/60 text-primary-foreground">
                           {follower.full_name?.split(' ').map(n => n[0]).join('') || 'U'}
                         </AvatarFallback>
                       </Avatar>
                       <div>
                         <h3 className="font-medium text-gray-900">{follower.full_name || 'Usuario'}</h3>
                         <p className="text-sm text-gray-600">@{follower.user_id.slice(0, 8)}</p>
                       </div>
                     </div>
                     
                     {!isOwnProfile && (
                       <div className="flex gap-2">
                         {getRelationshipStatus(follower.user_id) === 'none' && (
                           <Button
                             size="sm"
                             onClick={() => handleFollowBack(follower.user_id)}
                             className="text-xs"
                           >
                             Seguir
                           </Button>
                         )}
                         {getRelationshipStatus(follower.user_id) === 'they_follow_me' && (
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => handleFollowBack(follower.user_id)}
                             className="text-xs"
                           >
                             Seguir de vuelta
                           </Button>
                         )}
                         {getRelationshipStatus(follower.user_id) === 'mutual_following' && (
                           <div className="flex items-center gap-1 text-green-600 text-sm">
                             <UserCheck className="h-4 w-4" />
                             Amigos
                           </div>
                         )}
                         {getRelationshipStatus(follower.user_id) === 'i_follow_them' && (
                            <div className="flex items-center gap-1 text-primary text-sm">
                             <UserCheck className="h-4 w-4" />
                             Siguiendo
                           </div>
                         )}
                       </div>
                     )}
                     
                     {isOwnProfile && (
                       <>
                         {getRelationshipStatus(follower.user_id) === 'they_follow_me' && (
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => handleFollowBack(follower.user_id)}
                             className="text-xs"
                           >
                             Seguir de vuelta
                           </Button>
                         )}
                         {getRelationshipStatus(follower.user_id) === 'mutual_following' && (
                           <div className="flex items-center gap-1 text-green-600 text-sm">
                             <UserCheck className="h-4 w-4" />
                             Siguiendo mutuamente
                           </div>
                         )}
                         {getRelationshipStatus(follower.user_id) === 'i_follow_them' && (
                           <div className="flex items-center gap-1 text-primary text-sm">
                             <UserCheck className="h-4 w-4" />
                             Siguiendo
                           </div>
                         )}
                       </>
                     )}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : followers && followers.length > 0 ? (
            <div className="text-center py-8 text-gray-500">
              No se encontraron resultados para "{searchQuery}"
            </div>
           ) : (
             <div className="text-center py-8 text-gray-500">
               {isOwnProfile ? "No tienes seguidores aún" : "Este usuario no tiene seguidores aún"}
             </div>
           )}
        </div>
      </DialogContent>
    </Dialog>
  );
}